/**
 * reCAPTCHA utility functions for the Device Unlock Portal
 */

import { RECAPTCHA_CONSTANTS } from '@/lib/constants/appConstants';
import { RecaptchaError, RecaptchaTimeoutError } from '@/lib/errors/AppErrors';
import { getBrandFromDomain, getEnvironmentFromDomain } from './brandUtils';
import { log } from './logger';
import { logTokenAnalysis } from './tokenAnalysis';

/**
 * Check if reCAPTCHA is ready
 */
const isRecaptchaReady = (): boolean => {
  return window.grecaptcha?.enterprise?.execute && 
         typeof window.grecaptcha.enterprise.execute === 'function';
};

/**
 * Handle script element load event
 */
const handleScriptElementLoad = (
  scriptElement: Element, 
  resolveOnce: (value: boolean) => void
): void => {
  const handleLoad = () => {
    setTimeout(() => {
      if (isRecaptchaReady()) {
        resolveOnce(true);
      }
    }, 50);
  };

  if ((scriptElement as HTMLScriptElement & { readyState?: string }).readyState === 'complete' || 
      (scriptElement as HTMLScriptElement & { readyState?: string }).readyState === 'loaded') {
    handleLoad();
  } else {
    scriptElement.addEventListener('load', handleLoad, { once: true });
    scriptElement.addEventListener('error', () => {
      log.warn('reCAPTCHA script failed to load');
    }, { once: true });
  }
};

/**
 * Handle dynamic script injection detection
 */
const handleDynamicScript = (
  element: Element, 
  resolveOnce: (value: boolean) => void, 
  observer: MutationObserver
): void => {
  const src = element.getAttribute('src');
  if (element.tagName === 'SCRIPT' && src?.includes('recaptcha')) {
    element.addEventListener('load', () => {
      setTimeout(() => {
        if (isRecaptchaReady()) {
          resolveOnce(true);
          observer.disconnect();
        }
      }, 50);
    }, { once: true });
  }
};

/**
 * Process mutation record for dynamic script detection
 */
const processMutationRecord = (
  mutation: MutationRecord, 
  resolveOnce: (value: boolean) => void, 
  observer: MutationObserver
): void => {
  mutation.addedNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      handleDynamicScript(element, resolveOnce, observer);
    }
  });
};

/**
 * Setup mutation observer for dynamic script detection
 */
const setupMutationObserver = (
  resolveOnce: (value: boolean) => void, 
  timeout: number
): void => {
  if (typeof MutationObserver === 'undefined') return;

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      processMutationRecord(mutation, resolveOnce, observer);
    });
  });
  
  observer.observe(document.head || document.documentElement, {
    childList: true,
    subtree: true
  });
  
  setTimeout(() => observer.disconnect(), timeout);
};

/**
 * Calculate next poll interval with exponential backoff
 */
const calculatePollInterval = (
  currentInterval: number,
  consecutiveFailures: number,
  isSlowNetwork: boolean,
  maxInterval: number
): number => {
  const backoffMultiplier = isSlowNetwork ? 1.1 : 1.2;
  let newInterval = Math.min(Math.floor(currentInterval * backoffMultiplier), maxInterval);
  
  // For slow networks, implement adaptive polling based on consecutive failures
  if (isSlowNetwork && consecutiveFailures > 10) {
    newInterval = Math.min(newInterval * 1.5, maxInterval);
  }
  
  return newInterval;
};

/**
 * Check if polling should continue
 */
const shouldContinuePolling = (
  startTime: number,
  timeout: number,
  consecutiveFailures: number,
  isSlowNetwork: boolean
): { shouldContinue: boolean; elapsed: number } => {
  const currentTime = Date.now();
  const elapsed = currentTime - startTime;
  
  if (elapsed >= timeout) {
    log.warn(`reCAPTCHA polling timeout after ${timeout}ms (slow network: ${isSlowNetwork})`);
    return { shouldContinue: false, elapsed };
  }
  
  // Log progress occasionally for debugging slow network issues
  if (consecutiveFailures % 20 === 0) {
    log.debug(`reCAPTCHA polling progress: ${consecutiveFailures} attempts, ${elapsed}ms elapsed (slow network: ${isSlowNetwork})`);
  }
  
  return { shouldContinue: true, elapsed };
};

/**
 * Setup optimized polling with exponential backoff and enhanced network awareness
 */
const setupPolling = (
  resolveOnce: (value: boolean) => void, 
  startTime: number, 
  timeout: number,
  isSlowNetwork: boolean = false
): void => {
  const initialInterval = isSlowNetwork 
    ? RECAPTCHA_CONSTANTS.RETRIES.RETRY_INTERVAL_SLOW 
    : RECAPTCHA_CONSTANTS.RETRIES.RETRY_INTERVAL;
  
  let pollInterval: number = initialInterval;
  const maxPollInterval = RECAPTCHA_CONSTANTS.RETRIES.MAX_RETRY_INTERVAL;
  let consecutiveFailures = 0;
  
  const checkRecaptcha = () => {
    const { shouldContinue, elapsed } = shouldContinuePolling(startTime, timeout, consecutiveFailures, isSlowNetwork);
    
    if (!shouldContinue) {
      resolveOnce(false);
      return;
    }
    
    if (isRecaptchaReady()) {
      log.debug(`reCAPTCHA ready after ${elapsed}ms of polling (slow network: ${isSlowNetwork})`);
      resolveOnce(true);
      return;
    }
    
    consecutiveFailures++;
    pollInterval = calculatePollInterval(pollInterval, consecutiveFailures, isSlowNetwork, maxPollInterval);
    
    setTimeout(checkRecaptcha, pollInterval);
  };
  
  // Initial delay based on network speed - longer for slow networks to allow script download
  const initialDelay = isSlowNetwork ? initialInterval * 8 : initialInterval * 2;
  setTimeout(checkRecaptcha, initialDelay);
};

/**
 * Setup global callback strategy
 */
const setupGlobalCallback = (resolveOnce: (value: boolean) => void): void => {
  const windowWithCallback = window as Window & { onRecaptchaLoad?: () => void };
  const originalCallback = windowWithCallback.onRecaptchaLoad;
  windowWithCallback.onRecaptchaLoad = () => {
    if (originalCallback) originalCallback();
    if (isRecaptchaReady()) {
      resolveOnce(true);
    }
  };
};

/**
 * Wait for reCAPTCHA to be loaded and available using multiple strategies with network awareness
 * @param timeout - Maximum time to wait in milliseconds (default: auto-selected based on network)
 * @param isSlowNetwork - Whether the network is slow (affects timeouts and retry behavior)
 * @returns Promise that resolves to true if reCAPTCHA is available, false otherwise
 */
export const waitForRecaptcha = (
  timeout?: number,
  isSlowNetwork: boolean = false
): Promise<boolean> => {
  // Auto-select timeout based on network speed if not provided
  const defaultTimeout = isSlowNetwork 
    ? RECAPTCHA_CONSTANTS.TIMEOUTS.LOAD_WAIT_SLOW
    : RECAPTCHA_CONSTANTS.TIMEOUTS.LOAD_WAIT;
  
  const finalTimeout = timeout || defaultTimeout;
  
  return new Promise((resolve) => {
    const startTime = Date.now();
    let resolved = false;
    
    const resolveOnce = (value: boolean) => {
      if (resolved) return;
      resolved = true;
      resolve(value);
    };
    
    // Strategy 1: Immediate check
    if (isRecaptchaReady()) {
      resolveOnce(true);
      return;
    }
    
    // Strategy 2: Global callback
    setupGlobalCallback(resolveOnce);
    
    // Strategy 3: Monitor existing script tag
    const scriptElement = document.querySelector('script[src*="recaptcha"]');
    if (scriptElement) {
      handleScriptElementLoad(scriptElement, resolveOnce);
    }
    
    // Strategy 4: MutationObserver for dynamic scripts
    setupMutationObserver(resolveOnce, finalTimeout);
    
    // Strategy 5: Network-aware polling fallback
    setupPolling(resolveOnce, startTime, finalTimeout, isSlowNetwork);
  });
};

/**
 * Get timeout duration based on network speed
 */
const getExecutionTimeout = (timeout?: number, isSlowNetwork: boolean = false): number => {
  if (timeout) return timeout;
  
  return isSlowNetwork 
    ? RECAPTCHA_CONSTANTS.TIMEOUTS.EXECUTION_SLOW
    : RECAPTCHA_CONSTANTS.TIMEOUTS.EXECUTION;
};

/**
 * Execute reCAPTCHA with timeout wrapper
 */
const executeWithTimeout = (siteKey: string, action: string, finalTimeout: number): Promise<string> => {
  return Promise.race([
    window.grecaptcha.enterprise.execute(siteKey, { action }),
    new Promise<string>((_, reject) => 
      setTimeout(() => reject(new RecaptchaTimeoutError()), finalTimeout)
    )
  ]);
};

/**
 * Validate token length and return result
 */
const validateTokenLength = (token: string, minTokenLength: number, attemptNumber: number, elapsedTime: number): {
  isValid: boolean;
  tokenLength: number;
} => {
  const tokenLength = token ? token.length : 0;
  const isValid = Boolean(token && token.length >= minTokenLength);
  
  if (isValid) {
    log.debug(`reCAPTCHA token generated successfully (attempt ${attemptNumber}, length: ${token.length}, elapsed: ${elapsedTime}ms)`);
  } else {
    log.warn(`reCAPTCHA token too short (attempt ${attemptNumber}, length: ${tokenLength}, required: ${minTokenLength}, elapsed: ${elapsedTime}ms)`);
  }
  
  return { isValid, tokenLength };
};

/**
 * Calculate retry delay with time constraints
 */
const calculateRetryDelay = (
  elapsedTime: number, 
  maxRetryDuration: number, 
  isSlowNetwork: boolean
): { shouldRetry: boolean; delay: number; remainingTime: number } => {
  const remainingTime = maxRetryDuration - elapsedTime;
  
  if (elapsedTime >= maxRetryDuration) {
    return { shouldRetry: false, delay: 0, remainingTime };
  }
  
  const baseRetryDelay = isSlowNetwork 
    ? RECAPTCHA_CONSTANTS.VALIDATION.TOKEN_RETRY_DELAY_SLOW 
    : RECAPTCHA_CONSTANTS.VALIDATION.TOKEN_RETRY_DELAY;
  
  const retryDelay = Math.min(baseRetryDelay, remainingTime - 100); // Leave 100ms buffer
  
  return { 
    shouldRetry: retryDelay > 0, 
    delay: retryDelay, 
    remainingTime 
  };
};

/**
 * Handle retry logic for short tokens
 */
const handleTokenRetry = async (
  elapsedTime: number,
  maxRetryDuration: number,
  isSlowNetwork: boolean,
  executeAttempt: () => Promise<string>
): Promise<string> => {
  const { shouldRetry, delay, remainingTime } = calculateRetryDelay(elapsedTime, maxRetryDuration, isSlowNetwork);
  
  if (shouldRetry) {
    log.info(`Retrying reCAPTCHA execution in ${delay}ms due to short token (${remainingTime}ms remaining)...`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return executeAttempt();
  }
  
  log.warn(`Not enough time remaining for retry (${remainingTime}ms left)`);
  throw new RecaptchaError(`Time limit of ${maxRetryDuration}ms exceeded for token retry.`);
};

/**
 * Handle execution errors and convert them appropriately
 */
const handleExecutionError = (error: unknown, attemptNumber: number, elapsedTime: number): never => {
  log.error(`reCAPTCHA execution failed (attempt ${attemptNumber}, elapsed: ${elapsedTime}ms):`, error instanceof Error ? error.message : String(error));
  
  // Re-throw custom errors as-is
  if (error instanceof RecaptchaError || error instanceof RecaptchaTimeoutError) {
    throw error;
  }
  
  // Convert other errors to RecaptchaError
  const errorMessage = error instanceof Error ? error.message : 'reCAPTCHA verification failed';
  throw new RecaptchaError(errorMessage);
};

/**
 * Execute reCAPTCHA with timeout and error handling, with network awareness and token length validation
 * @param siteKey - The reCAPTCHA site key
 * @param action - The action to perform (e.g., 'login', 'submit')
 * @param timeout - Timeout in milliseconds (default: auto-selected based on network)
 * @param isSlowNetwork - Whether the network is slow (affects timeouts)
 * @param minTokenLength - Minimum acceptable token length (default: from constants)
 * @param maxRetryDuration - Maximum time to spend retrying for short tokens in milliseconds (default: 5000ms)
 * @returns Promise that resolves to the reCAPTCHA token
 * @throws {RecaptchaError} When reCAPTCHA verification fails
 * @throws {RecaptchaTimeoutError} When reCAPTCHA verification times out
 */
export const executeRecaptcha = async (
  siteKey: string, 
  action: string, 
  timeout?: number,
  isSlowNetwork: boolean = false,
  minTokenLength: number = RECAPTCHA_CONSTANTS.VALIDATION.MIN_TOKEN_LENGTH,
  maxRetryDuration: number = 5000 // 5 seconds maximum retry time
): Promise<string> => {
  const retryStartTime = Date.now();
  let attemptNumber = 0;
  
  const executeAttempt = async (): Promise<string> => {
    attemptNumber++;
    const currentTime = Date.now();
    const elapsedTime = currentTime - retryStartTime;
    
    try {
      const finalTimeout = getExecutionTimeout(timeout, isSlowNetwork);
      
      // Wait for reCAPTCHA to be available with network awareness
      const isAvailable = await waitForRecaptcha(undefined, isSlowNetwork);
      if (!isAvailable) {
        log.warn(`reCAPTCHA not available after waiting (attempt ${attemptNumber}, elapsed: ${elapsedTime}ms)`);
        throw new RecaptchaError('reCAPTCHA failed to load. Please refresh the page and try again.');
      }

      const token = await executeWithTimeout(siteKey, action, finalTimeout);
      
      // Log token analysis for debugging
      logTokenAnalysis(token, `attempt ${attemptNumber} (elapsed: ${elapsedTime}ms)`, minTokenLength);
      
      // Validate token length
      const { isValid, tokenLength } = validateTokenLength(token, minTokenLength, attemptNumber, elapsedTime);
      
      if (isValid) {
        return token;
      }
      
      // Try to retry if time allows
      try {
        return await handleTokenRetry(elapsedTime, maxRetryDuration, isSlowNetwork, executeAttempt);
      } catch (retryError) {
        // If retry failed, throw with comprehensive error message
        const errorMessage = `reCAPTCHA token too short after ${attemptNumber} attempts in ${elapsedTime}ms (${tokenLength} < ${minTokenLength} characters). ${retryError instanceof Error ? retryError.message : String(retryError)}`;
        throw new RecaptchaError(errorMessage);
      }
    } catch (error) {
      // If it's a token length error, let it bubble up for retry handling
      if (error instanceof RecaptchaError && error.message.includes('token too short')) {
        throw error;
      }
      
      handleExecutionError(error, attemptNumber, elapsedTime);
      // This line should never be reached as handleExecutionError always throws
      throw new RecaptchaError('Unexpected error in reCAPTCHA execution');
    }
  };

  return executeAttempt();
};

/**
 * Check if the network connection is slow
 * @returns boolean indicating if connection is considered slow
 */
export const isSlowConnection = (): boolean => {
  if (!('connection' in navigator)) {
    return false;
  }

  const navigatorWithConnection = navigator as Navigator & {
    connection?: {
      effectiveType?: string;
    };
  };
  
  const connection = navigatorWithConnection.connection;
  const slowConnections = ['slow-2g', '2g', '3g'];
  
  return connection?.effectiveType ? slowConnections.includes(connection.effectiveType) : false;
};

/**
 * Detect if the user has a slow network connection and return a promise
 * @param checkDuration - Duration to check connection in milliseconds (default: 2000)
 * @returns Promise that resolves to boolean indicating slow connection
 */
export const detectSlowNetwork = (checkDuration = 2000): Promise<boolean> => {
  return new Promise((resolve) => {
    // If network API is not available, assume normal connection
    if (!('connection' in navigator)) {
      resolve(false);
      return;
    }

    const navigatorWithConnection = navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        addEventListener?: (event: string, listener: () => void) => void;
        removeEventListener?: (event: string, listener: () => void) => void;
      };
    };
    
    const connection = navigatorWithConnection.connection;
    const slowConnections = ['slow-2g', '2g', '3g'];
    
    // Check initial state
    const initialSlow = connection?.effectiveType ? slowConnections.includes(connection.effectiveType) : false;
    
    // If already detected as slow, return immediately
    if (initialSlow) {
      resolve(true);
      return;
    }
    
    // Monitor for changes during check period
    let isDetectedSlow = false;
    
    const handleChange = () => {
      if (connection?.effectiveType && slowConnections.includes(connection.effectiveType)) {
        isDetectedSlow = true;
      }
    };
    
    if (connection?.addEventListener) {
      connection.addEventListener('change', handleChange);
    }
    
    // Check after specified duration
    setTimeout(() => {
      if (connection?.removeEventListener) {
        connection.removeEventListener('change', handleChange);
      }
      resolve(isDetectedSlow);
    }, checkDuration);
  });
};

/**
 * Create DNS prefetch and preconnect hints for better performance on slow networks
 */
const createPerformanceHints = (): void => {
  // DNS prefetch for reCAPTCHA domains
  const dnsPrefetch = document.createElement('link');
  dnsPrefetch.rel = 'dns-prefetch';
  dnsPrefetch.href = '//www.google.com';
  document.head.appendChild(dnsPrefetch);
  
  const dnsPrefetch2 = document.createElement('link');
  dnsPrefetch2.rel = 'dns-prefetch';
  dnsPrefetch2.href = '//www.gstatic.com';
  document.head.appendChild(dnsPrefetch2);
  
  // Preconnect for faster resource loading
  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = 'https://www.google.com';
  preconnect.crossOrigin = 'anonymous';
  document.head.appendChild(preconnect);
};

/**
 * Get the current runtime origin when available.
 */
const getRuntimeOrigin = (): string | undefined => {
  const origin = globalThis.location?.origin ?? globalThis.window?.location?.origin;
  return origin?.trim() || undefined;
};

/**
 * Wait briefly for the browser origin to become available before resolving environment-specific settings.
 */
const waitForOrigin = async (
  timeout: number = 1000,
  pollInterval: number = 25
): Promise<string | undefined> => {
  const initialOrigin = getRuntimeOrigin();
  if (initialOrigin) {
    return initialOrigin;
  }

  if (globalThis.window === undefined) {
    return undefined;
  }

  const startTime = Date.now();

  return new Promise((resolve) => {
    const checkOrigin = () => {
      const currentOrigin = getRuntimeOrigin();

      if (currentOrigin || Date.now() - startTime >= timeout) {
        resolve(currentOrigin);
        return;
      }

      setTimeout(checkOrigin, pollInterval);
    };

    checkOrigin();
  });
};
const getBrandKey = (brandKeyName?: string): string | undefined  =>{
  if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_STRAIGHTTALK'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_STRAIGHTTALK
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_STRAIGHTTALK_TEST'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_STRAIGHTTALK_TEST
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_STRAIGHTTALK_PROD'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_STRAIGHTTALK_PROD
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TOTALWIRELESS'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TOTALWIRELESS
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TOTALWIRELESS_TEST'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TOTALWIRELESS_TEST
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TOTALWIRELESS_PROD'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TOTALWIRELESS_PROD
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TRACFONE'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TRACFONE
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TRACFONE_TEST'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TRACFONE_TEST
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TRACFONE_PROD'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TRACFONE_PROD
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_SIMPLEMOBILE'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_SIMPLEMOBILE
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_SIMPLEMOBILE_TEST'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_SIMPLEMOBILE_TEST
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_SIMPLEMOBILE_PROD'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_SIMPLEMOBILE_PROD
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_FAMILYMOBILE'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_FAMILYMOBILE
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_FAMILYMOBILE_TEST'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_FAMILYMOBILE_TEST
  }else if(brandKeyName === 'NEXT_PUBLIC_RECAPTCHA_SITE_KEY_FAMILYMOBILE_PROD'){
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_FAMILYMOBILE_PROD
  }
  return undefined;
}
/**
 * Resolve the reCAPTCHA site key for a given runtime origin.
 */
const resolveRecaptchaSiteKey = (origin?: string): string | undefined => {
  if (!origin) {
    return undefined;
  }

  const brand = getBrandFromDomain(origin);
  const environment = getEnvironmentFromDomain(origin);

  let envSuffix = '';
  if (environment === 'PROD') {
    envSuffix = '_PROD';
  } else if (environment === 'TEST') {
    envSuffix = '_TEST';
  }
  // DIT1 uses no suffix (default keys)

  if (brand) {
    const brandKeyName = `NEXT_PUBLIC_RECAPTCHA_SITE_KEY_${brand}${envSuffix}`;
    const brandSiteKey = getBrandKey(brandKeyName);
    
    if (brandSiteKey) {
      return brandSiteKey;
    }
  } else if (origin.includes('localhost')) {
    return process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_TOTALWIRELESS;
  }

  return undefined;
};

/**
 * Resolve the reCAPTCHA site key for the current runtime origin.
 */
export const getRecaptchaSiteKey = async (): Promise<string | undefined> => {
  const origin = await waitForOrigin();
  console.log('[reCAPTCHA] getRecaptchaSiteKey called with origin:', origin);
  const siteKey = resolveRecaptchaSiteKey(origin);
  console.log('[reCAPTCHA] Resolved site key:', siteKey ? `${siteKey.substring(0, 20)}...` : 'NONE');
  return siteKey;
};

/**
 * Enhanced preload reCAPTCHA script with comprehensive slow network optimizations
 * @param isSlowNetwork - Whether the network is detected as slow
 * @param retryAttempt - Current retry attempt (for internal use)
 * @returns Promise that resolves when preload attempt is complete
 */
export const preloadRecaptchaScript = async (
  isSlowNetwork: boolean = false, 
  retryAttempt: number = 0
): Promise<void> => {
  try {
    console.log('[reCAPTCHA] preloadRecaptchaScript called (slow network:', isSlowNetwork, ', attempt:', retryAttempt + 1, ')');
    const siteKey = await getRecaptchaSiteKey();
    if (!siteKey) {
      console.warn('[reCAPTCHA] ⚠️ Site key not configured, skipping preload');
      log.warn('reCAPTCHA site key not configured, skipping preload');
      return;
    }
    
    // Check if script is already loaded and working
    if (document.querySelector('script[src*="recaptcha"]') && isRecaptchaReady()) {
      console.log('[reCAPTCHA] Script already loaded and ready, skipping preload');
      log.debug('reCAPTCHA script already loaded and ready, skipping preload');
      return;
    }
    
    // Create performance hints on first attempt
    if (retryAttempt === 0) {
      createPerformanceHints();
    }
    
    // Remove any failed/stale script tags before retry
    if (retryAttempt > 0) {
      const existingScript = document.querySelector('script[src*="recaptcha"]');
      if (existingScript && !isRecaptchaReady()) {
        existingScript.remove();
        log.debug(`Removed stale reCAPTCHA script on retry attempt ${retryAttempt}`);
      }
    }
    
    // Create script element with enhanced network-aware settings
    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    
    // Enhanced loading strategy for slow networks
    if (isSlowNetwork) {
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&onload=onRecaptchaLoad&hl=en`;
      // Add explicit timeout and retry handling for slow networks
      const timeoutDuration = 15000; // 15 seconds for slow networks
      const timeoutId = setTimeout(() => {
        log.warn(`reCAPTCHA script load timeout on slow network (attempt ${retryAttempt + 1})`);
        script.remove();
        // Retry up to 2 more times with exponential backoff
        if (retryAttempt < 2) {
          const retryDelay = (retryAttempt + 1) * 2000; // 2s, 4s delays
          setTimeout(() => {
            preloadRecaptchaScript(isSlowNetwork, retryAttempt + 1);
          }, retryDelay);
        }
      }, timeoutDuration);
      
      script.onload = () => {
        clearTimeout(timeoutId);
        log.debug(`reCAPTCHA script loaded successfully on slow network (attempt ${retryAttempt + 1})`);
      };
      
      script.onerror = () => {
        clearTimeout(timeoutId);
        log.warn(`reCAPTCHA script failed to load on slow network (attempt ${retryAttempt + 1})`);
        // Retry with different approach
        if (retryAttempt < 2) {
          const retryDelay = (retryAttempt + 1) * 3000; // 3s, 6s delays
          setTimeout(() => {
            preloadRecaptchaScript(isSlowNetwork, retryAttempt + 1);
          }, retryDelay);
        }
      };
    } else {
      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}&onload=onRecaptchaLoad`;
      script.onload = () => {
        log.debug('reCAPTCHA script loaded successfully');
      };
      script.onerror = () => {
        log.warn('reCAPTCHA script failed to load');
      };
    }
    
    // Add to head for faster loading
    document.head.appendChild(script);
    
    console.log('[reCAPTCHA] ✅ Script preload initiated (slow network:', isSlowNetwork, ', attempt:', retryAttempt + 1, ')');
    log.debug(`reCAPTCHA script preload initiated (slow network: ${isSlowNetwork}, attempt: ${retryAttempt + 1})`);
  } catch (error) {
    console.error('[reCAPTCHA] ❌ Failed to preload script (attempt', retryAttempt + 1, '):', error);
    log.error(`Failed to preload reCAPTCHA script (attempt ${retryAttempt + 1}):`, error);
  }
};
